import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Modal, Button, Table, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { getSocialFeeDisplayRate } from '../enums/socialFeeRateMapping.enum';
import { formatAmountWithLocale } from '../helper/common.helper';
import './calculation-breakdown-modal.scss';

// Safe to call repeatedly — registers what the chart tab needs
ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, ChartTooltip, ChartLegend);

const CBM_PALETTE = [
  '#7c3aed', '#a855f7', '#c084fc', '#8b5cf6', '#6366f1',
  '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6',
];

// Freelancer avatar initials, e.g. "ManishK" → "MK", "John Doe" → "JD"
const getInitials = (name?: string): string => {
  if (!name) return '';
  const caps = name.match(/[A-Z]/g) || [];
  if (caps.length >= 2) return (caps[0] + caps[caps.length - 1]);
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase();
};

interface CalculationBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  calculationData: any;
  currency?: string;
}

const CalculationBreakdownModal: React.FC<CalculationBreakdownModalProps> = ({
  isOpen,
  onClose,
  calculationData,
  currency = 'SEK'
}) => {
  const { t, i18n } = useTranslation();
  
  // The API response structure is directly in calculationData
  const data = calculationData?.response || null;
  
  // Determine if we should use SEK values (when currency is not SEK)
  const isForeignCurrency = data?.totalCalculation?.currencyName && data.totalCalculation.currencyName !== 'SEK';
  
  // Format currency - always show SEK when foreign currency
  const formatCurrency = (amount: number) => {
    if (amount === 0 || amount === null || amount === undefined) {
      return formatAmountWithLocale(0, 'SEK', i18n.language);
    }
    return formatAmountWithLocale(amount, 'SEK', i18n.language);
  };
  
  // Format original currency (for Invoice Amount and VAT fields)
  const formatCurrencyOriginal = (amount: number) => {
    if (amount === 0 || amount === null || amount === undefined) {
      return formatAmountWithLocale(0, currency, i18n.language);
    }
    return formatAmountWithLocale(amount, currency, i18n.language);
  };
  
  // Helper to get SEK amount
  const getAmount = (directAmount: number, sekAmount: number) => {
    return isForeignCurrency ? sekAmount : directAmount;
  };

  // ── Breakdown (list) vs Chart tab ──────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'breakdown' | 'chart'>('breakdown');
  // Which freelancer the Chart tab shows — 'all' (combined) or a freelancer index
  const [chartFreelancer, setChartFreelancer] = useState<string>('all');
  const [chartDropdownOpen, setChartDropdownOpen] = useState(false);
  const chartDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOpen) { setActiveTab('breakdown'); setChartFreelancer('all'); }
  }, [isOpen]);
  // Close the freelancer dropdown on outside click
  useEffect(() => {
    if (!chartDropdownOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (chartDropdownRef.current && !chartDropdownRef.current.contains(e.target as Node)) {
        setChartDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [chartDropdownOpen]);

  // Sliding pill-tab indicator
  const tabsRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  const [indicatorReady, setIndicatorReady] = useState(false);
  const measureIndicator = useCallback(() => {
    const container = tabsRef.current;
    if (!container) return;
    const activeBtn = container.querySelector<HTMLElement>('.cbm-tab.active');
    if (activeBtn) setIndicator({ left: activeBtn.offsetLeft, width: activeBtn.offsetWidth });
  }, []);
  useLayoutEffect(() => {
    measureIndicator();
  }, [activeTab, isOpen, measureIndicator]);
  useEffect(() => {
    if (!isOpen) {
      setIndicatorReady(false);
      return;
    }
    measureIndicator();
    const raf = requestAnimationFrame(() => {
      measureIndicator();
      setIndicatorReady(true);
    });
    const timers = [80, 260, 500].map((ms) => setTimeout(measureIndicator, ms));
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [isOpen, measureIndicator]);
  useEffect(() => {
    window.addEventListener('resize', measureIndicator);
    return () => window.removeEventListener('resize', measureIndicator);
  }, [measureIndicator]);

  // Build chart data (doughnut composition + full-list bar) from a calc object
  const money = (direct: number, sek: number) => getAmount(direct, sek);
  const buildChart = (c: any) => {
    const composition = [
      { label: t('NET_SALARY') || 'Net Salary', value: money(c.net, c.netInSek || c.net) },
      { label: t('INCOME_TAX') || 'Income Tax', value: money(c.tax, c.taxInSek || c.tax) },
      { label: t('HOLIDAY_PAY') || 'Holiday Pay', value: money(c.holidayPayAmount, c.holidayPayAmountInSek || c.holidayPayAmount) },
      { label: t('SOCIAL_FEE') || 'Social Fee', value: money(c.socialFee, c.socialFeeInSek || c.socialFee) },
      { label: t('PENSION_SOCIAL_FEE') || 'Pension Social Fee', value: money(c.pensionSocialFee, c.pensionSocialFeeInSek || c.pensionSocialFee) },
      { label: t('PENSION') || 'Pension', value: money(c.pension, c.pensionInSek || c.pension) },
      { label: t('INVOZIO_FEE') || 'Invozio Fee', value: money(c.fee, c.feeInSEK || c.fee) },
      { label: t('EXPRESS_FEE') || 'Express Fee', value: money(c.expressFee, c.expressFeeInSek || c.expressFee) },
    ].filter((x) => typeof x.value === 'number' && !isNaN(x.value) && x.value > 0);

    const doughnutData = {
      labels: composition.map((x) => x.label),
      datasets: [{
        data: composition.map((x) => x.value),
        backgroundColor: composition.map((_, i) => CBM_PALETTE[i % CBM_PALETTE.length]),
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 8,
      }],
    };

    const barSource: Array<{ label: string; value: number }> = [];
    const push = (label: string, v: number) => { if (typeof v === 'number' && !isNaN(v) && v !== 0) barSource.push({ label, value: v }); };
    push(t('INVOICE_AMOUNT_INCLUDING_VAT') || 'Invoice Amount inc. VAT', c.amountIncludingVat);
    if (c.rutDeductionAmount > 0) push(t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction', -money(c.rutDeductionAmount, c.rutDeductionAmount));
    if (c.rootDeductionAmount > 0) push(t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction', -money(c.rootDeductionAmount, c.rootDeductionAmount));
    if (c.vat > 0) push(`${t('VAT') || 'VAT'} (${c.vatPercentage}%)`, c.vat);
    push(t('INVOICE_AMOUNT_EXCLUDING_VAT') || 'Invoice amount ex. VAT', money(c.amount, c.amountInSek || c.amount));
    if (c.fee > 0) push(t('INVOZIO_FEE') || 'Invozio Fee', money(c.fee, c.feeInSEK || c.fee));
    if (c.socialFee > 0) push(t('SOCIAL_FEE') || 'Social Fee', money(c.socialFee, c.socialFeeInSek || c.socialFee));
    if (c.pensionSocialFee > 0) push(t('PENSION_SOCIAL_FEE') || 'Pension Social Fee', money(c.pensionSocialFee, c.pensionSocialFeeInSek || c.pensionSocialFee));
    if (c.pension > 0) push(t('PENSION') || 'Pension', money(c.pension, c.pensionInSek || c.pension));
    push(t('GROSS_SALARY_INCLUDING_HOLIDAY_PAY') || 'Gross salary', money(c.gross, c.grossInSek || c.gross));
    if (c.tax > 0) push(t('INCOME_TAX') || 'Income Tax', money(c.tax, c.taxInSek || c.tax));
    if (c.holidayPayAmount > 0) push(t('HOLIDAY_PAY') || 'Holiday Pay', money(c.holidayPayAmount, c.holidayPayAmountInSek || c.holidayPayAmount));
    if (c.expressFee > 0) push(t('EXPRESS_FEE') || 'Express Fee', money(c.expressFee, c.expressFeeInSek || c.expressFee));
    push(t('NET_SALARY') || 'Net Salary', money(c.net, c.netInSek || c.net));

    const barData = {
      labels: barSource.map((x) => x.label),
      datasets: [{
        label: t('AMOUNT') || 'Amount',
        data: barSource.map((x) => x.value),
        backgroundColor: barSource.map((x) => (x.value < 0 ? '#ef4444' : '#7c3aed')),
        borderRadius: 6,
        maxBarThickness: 22,
      }],
    };
    return { composition, doughnutData, barSource, barData };
  };

  const chartTooltip = {
    callbacks: { label: (ctx: any) => ` ${formatCurrency(Math.abs(Number(ctx.parsed?.y ?? ctx.parsed?.x ?? ctx.parsed ?? ctx.raw)))}` },
  };
  const doughnutOptions: any = {
    responsive: true, maintainAspectRatio: false, cutout: '64%',
    plugins: { legend: { display: false }, tooltip: chartTooltip },
    animation: { animateScale: true, animateRotate: true, duration: 700 },
  };
  const barOptions: any = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: chartTooltip },
    animation: { duration: 700, easing: 'easeOutQuart' },
    scales: {
      x: { grid: { color: 'rgba(124,58,237,0.08)' }, ticks: { color: '#6b6480', font: { size: 11 } } },
      y: { grid: { display: false }, ticks: { color: '#1a1330', font: { size: 11, weight: '600' } } },
    },
  };

  const renderChartView = () => {
    const freelancers: any[] = Array.isArray(data?.freelancersData) ? data.freelancersData : [];
    const isMulti = freelancers.length > 1;
    // 'all' → combined totals, otherwise the selected freelancer's own figures
    const selectedIdx = chartFreelancer === 'all' ? -1 : Number(chartFreelancer);
    const c = (selectedIdx >= 0 && freelancers[selectedIdx]) ? freelancers[selectedIdx] : data?.totalCalculation;
    if (!c) return <div className="no-data"><h5>{t('NO_DATA_AVAILABLE') || 'No data available'}</h5></div>;
    const { composition, doughnutData, barSource, barData } = buildChart(c);
    return (
      <div className="cbm-chart-view">
        {isMulti && (() => {
          const currentLabel = chartFreelancer === 'all'
            ? (t('ALL') || 'All')
            : (freelancers[selectedIdx]?.freelancerName || `Freelancer ${selectedIdx + 1}`);
          const options = [
            { value: 'all', label: (t('ALL') as string) || 'All', name: undefined as string | undefined },
            ...freelancers.map((f: any, i: number) => ({
              value: String(i),
              label: f.freelancerName || `Freelancer ${i + 1}`,
              name: f.freelancerName as string | undefined,
            })),
          ];
          return (
            <div className="cbm-chart-filter">
              <label>{t('SHOW_DATA_FOR') || 'Show data for'}</label>
              <div className={`cbm-fl-dropdown${chartDropdownOpen ? ' open' : ''}`} ref={chartDropdownRef}>
                <button
                  type="button"
                  className="cbm-fl-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={chartDropdownOpen}
                  onClick={() => setChartDropdownOpen((o) => !o)}
                >
                  <span className="cbm-fl-avatar">
                    {chartFreelancer === 'all'
                      ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                      : (getInitials(freelancers[selectedIdx]?.freelancerName) || (selectedIdx + 1))}
                  </span>
                  <span className="cbm-fl-current">{currentLabel}</span>
                  <svg className="cbm-fl-chevron" width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {chartDropdownOpen && (
                  <div className="cbm-fl-list" role="listbox">
                    {options.map((opt) => {
                      const isSel = chartFreelancer === opt.value;
                      return (
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSel}
                          key={opt.value}
                          className={`cbm-fl-option${isSel ? ' selected' : ''}`}
                          onClick={() => { setChartFreelancer(opt.value); setChartDropdownOpen(false); }}
                        >
                          <span className="cbm-fl-avatar">
                            {opt.value === 'all'
                              ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                              : (getInitials(opt.name) || opt.label.slice(0, 2).toUpperCase())}
                          </span>
                          <span className="cbm-fl-option-label">{opt.label}</span>
                          {isSel && (
                            <svg className="cbm-fl-check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                              <path d="M13.5 4.5L6.5 11.5L2.5 7.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        {composition.length > 0 && (
          <div className="cbm-chart-card">
            <h6 className="cbm-chart-title">{t('SALARY_DISTRIBUTION') || 'How the amount is distributed'}</h6>
            <div className="cbm-doughnut-wrap">
              <div className="cbm-doughnut">
                <Doughnut data={doughnutData} options={doughnutOptions} />
                <div className="cbm-doughnut-center">
                  <span className="cbm-center-label">{t('NET_SALARY') || 'Net Salary'}</span>
                  <span className="cbm-center-value">{formatCurrency(getAmount(c.net, c.netInSek || c.net))}</span>
                </div>
              </div>
              <div className="cbm-legend">
                {composition.map((item, i) => (
                  <div className="cbm-legend-row" key={item.label}>
                    <span className="cbm-legend-dot" style={{ background: CBM_PALETTE[i % CBM_PALETTE.length] }} />
                    <span className="cbm-legend-label">{item.label}</span>
                    <span className="cbm-legend-value">{formatCurrency(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {barSource.length > 0 && (
          <div className="cbm-chart-card">
            <h6 className="cbm-chart-title">{t('ALL_AMOUNTS') || 'All amounts'}</h6>
            <div className="cbm-bar" style={{ height: `${Math.max(220, barSource.length * 34)}px` }}>
              <Bar data={barData} options={barOptions} />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render single freelancer view (structured with sections)
  const renderSingleFreelancerView = (calcData: any) => (
    <div className="calculation-breakdown-single">
      <div className="breakdown-section">
        {/* Step 1: Invoice amount inc. VAT */}
        <div className="calculation-item">
          <span className="item-label">{t('INVOICE_AMOUNT_INCLUDING_VAT') || 'Invoice Amount inc. VAT'}</span>
          <span className="item-value">{formatCurrencyOriginal(calcData.amountIncludingVat)}</span>
        </div>

        {/* Step 2: RUT/ROT Deduction (if applicable) */}
        {calcData.rutDeductionAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction'}</span>
            <span className="item-value">-{formatCurrency(calcData.rutDeductionAmount)}</span>
          </div>
        )}

        {calcData.rootDeductionAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction'}</span>
            <span className="item-value">-{formatCurrency(calcData.rootDeductionAmount)}</span>
          </div>
        )}

        {/* Step 3: Customer will pay (if RUT/ROT deduction exists) */}
        {(calcData.rutDeductionAmount > 0 || calcData.rootDeductionAmount > 0) && (
          <div className="calculation-item">
            <span className="item-label">{t('CUSTOMER_WILL_PAY') || 'Customer will pay'}</span>
            <span className="item-value">{formatCurrency(calcData.amountIncludingVatAfterHouseholdServiceDeduction)}</span>
          </div>
        )}

        {/* Step 4: VAT */}
        {calcData.vat > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('VAT') || 'VAT'} ({calcData.vatPercentage}%)</span>
            <span className="item-value">{formatCurrencyOriginal(calcData.vat)}</span>
          </div>
        )}

        {/* Step 5: Invoice amount ex. VAT */}
        <div className="calculation-item">
          <span className="item-label">{t('INVOICE_AMOUNT_EXCLUDING_VAT') || 'Invoice amount ex. VAT'}</span>
          <span className="item-value">{formatCurrency(getAmount(calcData.amount, calcData.amountInSek || calcData.amount))}</span>
        </div>

        {/* Step 6: Invozio percent fee */}
        {calcData.fee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('INVOZIO_FEE') || 'Invozio percent fee'} ({calcData.fakturaFeePercentage}%)</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.fee, calcData.feeInSEK || calcData.fee))}</span>
          </div>
        )}

        {/* Step 7: Loyalty discount on invozio fee */}
        {calcData.loyaltyDiscountPercentage > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('LOYALTY_DISCOUNT_ON_INVOZIO_FEE') || 'Loyalty discount on invozio fee'}</span>
            <span className="item-value">{calcData.loyaltyDiscountPercentage}%</span>
          </div>
        )}

        {/* Step 8: Social Fee */}
        {calcData.socialFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">
              {t('SOCIAL_FEE') || 'Social Fee'} ({getSocialFeeDisplayRate(calcData.socialFeeRateDeduction).toFixed(2)}%)
            </span>
            <span className="item-value">{formatCurrency(getAmount(calcData.socialFee, calcData.socialFeeInSek || calcData.socialFee))}</span>
          </div>
        )}

        {/* Step 9: Pension Social Fee */}
        {calcData.pensionSocialFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('PENSION_SOCIAL_FEE') || 'Pension Social Fee'} ({calcData.pensionFeePer}%)</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.pensionSocialFee, calcData.pensionSocialFeeInSek || calcData.pensionSocialFee))}</span>
          </div>
        )}

        {/* Step 10: Pension */}
        {calcData.pension > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('PENSION') || 'Pension'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.pension, calcData.pensionInSek || calcData.pension))}</span>
          </div>
        )}

        {/* Step 11: Gross salary incl. holiday pay */}
        <div className="calculation-item">
          <span className="item-label">{t('GROSS_SALARY_INCLUDING_HOLIDAY_PAY') || 'Gross salary incl. holiday pay'}</span>
          <span className="item-value">{formatCurrency(getAmount(calcData.gross, calcData.grossInSek || calcData.gross))}</span>
        </div>

        {/* Step 12: Tax */}
        {calcData.tax > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('INCOME_TAX') || 'Tax'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.tax, calcData.taxInSek || calcData.tax))}</span>
          </div>
        )}

        {/* Step 13: Holiday Pay */}
        {calcData.holidayPayAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('HOLIDAY_PAY') || 'Holiday Pay'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.holidayPayAmount, calcData.holidayPayAmountInSek || calcData.holidayPayAmount))}</span>
          </div>
        )}

        {/* Step 14: Express Fee */}
        {calcData.expressFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('EXPRESS_FEE') || 'Express Fee'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.expressFee, calcData.expressFeeInSek || calcData.expressFee))}</span>
          </div>
        )}

        {/* Step 15: Total Working Hours */}
        {calcData.workingHours && calcData.workingHours.length > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('TOTAL_WORKING_HOURS') || 'Total Working Hours'}</span>
            <span className="item-value">{calcData.workingHours.reduce((sum: number, wh: any) => sum + (wh.workingHour || 0), 0)} {t('HOURS') || 'Hours'}</span>
          </div>
        )}

        {/* Step 16: Tax-free articles */}
        {calcData.mileageTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('MILEAGE_REIMBURSEMENT') || 'Mileage Reimbursement'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.mileageTaxFreeAmount, calcData.mileageTaxFreeAmountInSek || calcData.mileageTaxFreeAmount))}</span>
          </div>
        )}

        {calcData.expenditureForMaterialsAndToolsTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('EXPENSES_FOR_MATERIALS_AND_TOOLS') || 'Material Tool & Expenses'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.expenditureForMaterialsAndToolsTaxFreeAmount, calcData.expenditureForMaterialsAndToolsTaxFreeAmountInSek || calcData.expenditureForMaterialsAndToolsTaxFreeAmount))}</span>
          </div>
        )}

        {calcData.allowanceAbroadTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.allowanceAbroadTaxFreeAmount, calcData.allowanceAbroadTaxFreeAmountInSek || calcData.allowanceAbroadTaxFreeAmount))}</span>
          </div>
        )}

        {calcData.domesticAllowanceFullDayTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('FULL_DAY') || 'Full Day'})</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.domesticAllowanceFullDayTaxFreeAmount, calcData.domesticAllowanceFullDayTaxFreeAmountInSek || calcData.domesticAllowanceFullDayTaxFreeAmount))}</span>
          </div>
        )}

        {calcData.domesticAllowanceHalfDayTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('HALF_DAY') || 'Half Day'})</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.domesticAllowanceHalfDayTaxFreeAmount, calcData.domesticAllowanceHalfDayTaxFreeAmountInSek || calcData.domesticAllowanceHalfDayTaxFreeAmount))}</span>
          </div>
        )}

        {calcData.travelCostsAccordingTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('TRAVEL_COSTS_ACCORDING_TO_ATTACHED_PDF') || 'Travel costs according to attached PDF'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.travelCostsAccordingTaxFreeAmount, calcData.travelCostsAccordingTaxFreeAmountInSek || calcData.travelCostsAccordingTaxFreeAmount))}</span>
          </div>
        )}

        {calcData.allowance > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE') || 'Other Allowances'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.allowance, calcData.allowanceInSek || calcData.allowance))}</span>
          </div>
        )}

        {calcData.expense > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('EXPENSE') || 'Travel Expenses as per attached PDF'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.expense, calcData.expenseInSek || calcData.expense))}</span>
          </div>
        )}

        {calcData.administratorFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ADMINISTRATOR_FEE') || 'Administrator Fee'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.administratorFee, calcData.administratorFeeInSek || calcData.administratorFee))}</span>
          </div>
        )}
      </div>

      {/* Step 17: Net salary incl. holiday pay */}
      <div className="breakdown-section net-salary-section">
        <div className="calculation-item">
          <span className="item-label item-label-bold">{t('NET_SALARY') || 'Net salary incl. holiday pay'}</span>
          <span className="item-value">{formatCurrency(getAmount(calcData.net, calcData.netInSek || calcData.net))}</span>
        </div>
      </div>
    </div>
  );

  // Render multiple freelancers view (Invoice Summary + Individual Breakdowns)
  const renderMultipleFreelancersView = (totalCalc: any, freelancersData: any[]) => (
    <div className="calculation-breakdown-multiple">
      {/* Invoice Summary Section */}
      <div className="breakdown-section">
        <h6 className="section-title">{t('INVOICE_SUMMARY') || 'Invoice Summary'}</h6>
        
        {/* Step 1: Invoice amount inc. VAT */}
        <div className="calculation-item">
          <span className="item-label">{t('INVOICE_AMOUNT_INCLUDING_VAT') || 'Invoice Amount inc. VAT'}</span>
          <span className="item-value">{formatCurrencyOriginal(totalCalc.amountIncludingVat)}</span>
        </div>

        {/* Step 2: RUT/ROT Deduction */}
        {totalCalc.rutDeductionAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction'}</span>
            <span className="item-value">-{formatCurrency(totalCalc.rutDeductionAmount)}</span>
          </div>
        )}

        {totalCalc.rootDeductionAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction'}</span>
            <span className="item-value">-{formatCurrency(totalCalc.rootDeductionAmount)}</span>
          </div>
        )}

        {/* Step 3: Customer will pay */}
        {(totalCalc.rutDeductionAmount > 0 || totalCalc.rootDeductionAmount > 0) && (
          <div className="calculation-item">
            <span className="item-label">{t('CUSTOMER_WILL_PAY') || 'Customer will pay'}</span>
            <span className="item-value">{formatCurrency(totalCalc.amountIncludingVatAfterHouseholdServiceDeduction)}</span>
          </div>
        )}

        {/* Step 4: VAT */}
        {totalCalc.vat > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('VAT') || 'VAT'} ({totalCalc.vatPercentage}%)</span>
            <span className="item-value">{formatCurrencyOriginal(totalCalc.vat)}</span>
          </div>
        )}

        {/* Step 5: Invoice amount ex. VAT */}
        <div className="calculation-item">
          <span className="item-label">{t('INVOICE_AMOUNT_EXCLUDING_VAT') || 'Invoice amount ex. VAT'}</span>
          <span className="item-value">{formatCurrency(getAmount(totalCalc.amount, totalCalc.amountInSek || totalCalc.amount))}</span>
        </div>

        {/* Step 6: Invozio percent fee */}
        {totalCalc.fee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('INVOZIO_FEE') || 'Invozio percent fee'} ({totalCalc.fakturaFeePercentage}%)</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.fee, totalCalc.feeInSEK || totalCalc.fee))}</span>
          </div>
        )}

        {/* Step 7: Loyalty discount on invozio fee */}
        {totalCalc.loyaltyDiscountPercentage > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('LOYALTY_DISCOUNT_ON_INVOZIO_FEE') || 'Loyalty discount on invozio fee'}</span>
            <span className="item-value">{totalCalc.loyaltyDiscountPercentage}%</span>
          </div>
        )}

        {/* Step 8: Social Fee */}
        {totalCalc.socialFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">
              {t('SOCIAL_FEE') || 'Social Fee'} ({getSocialFeeDisplayRate(totalCalc.socialFeeRateDeduction).toFixed(2)}%)
            </span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.socialFee, totalCalc.socialFeeInSek || totalCalc.socialFee))}</span>
          </div>
        )}

        {/* Step 9: Pension Social Fee */}
        {totalCalc.pensionSocialFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('PENSION_SOCIAL_FEE') || 'Pension Social Fee'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.pensionSocialFee, totalCalc.pensionSocialFeeInSek || totalCalc.pensionSocialFee))}</span>
          </div>
        )}

        {/* Step 10: Pension */}
        {totalCalc.pension > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('PENSION') || 'Pension'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.pension, totalCalc.pensionInSek || totalCalc.pension))}</span>
          </div>
        )}

        {/* Step 11: Gross salary incl. holiday pay */}
        <div className="calculation-item">
          <span className="item-label">{t('GROSS_SALARY_INCLUDING_HOLIDAY_PAY') || 'Gross salary incl. holiday pay'}</span>
          <span className="item-value">{formatCurrency(getAmount(totalCalc.gross, totalCalc.grossInSek || totalCalc.gross))}</span>
        </div>

        {/* Step 12: Tax */}
        {totalCalc.tax > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('INCOME_TAX') || 'Tax'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.tax, totalCalc.taxInSek || totalCalc.tax))}</span>
          </div>
        )}

        {/* Step 13: Holiday Pay */}
        {totalCalc.holidayPayAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('HOLIDAY_PAY') || 'Holiday Pay'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.holidayPayAmount, totalCalc.holidayPayAmountInSek || totalCalc.holidayPayAmount))}</span>
          </div>
        )}

        {/* Step 14: Express Fee */}
        {totalCalc.expressFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('EXPRESS_FEE') || 'Express Fee'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.expressFee, totalCalc.expressFeeInSek || totalCalc.expressFee))}</span>
          </div>
        )}

        {/* Step 15: Tax-free articles */}
        {totalCalc.mileageTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('MILEAGE_REIMBURSEMENT') || 'Mileage Reimbursement'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.mileageTaxFreeAmount, totalCalc.mileageTaxFreeAmountInSek || totalCalc.mileageTaxFreeAmount))}</span>
          </div>
        )}

        {totalCalc.expenditureForMaterialsAndToolsTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('EXPENSES_FOR_MATERIALS_AND_TOOLS') || 'Material Tool & Expenses'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.expenditureForMaterialsAndToolsTaxFreeAmount, totalCalc.expenditureForMaterialsAndToolsTaxFreeAmountInSek || totalCalc.expenditureForMaterialsAndToolsTaxFreeAmount))}</span>
          </div>
        )}

        {totalCalc.allowanceAbroadTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.allowanceAbroadTaxFreeAmount, totalCalc.allowanceAbroadTaxFreeAmountInSek || totalCalc.allowanceAbroadTaxFreeAmount))}</span>
          </div>
        )}

        {totalCalc.domesticAllowanceFullDayTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('FULL_DAY') || 'Full Day'})</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.domesticAllowanceFullDayTaxFreeAmount, totalCalc.domesticAllowanceFullDayTaxFreeAmountInSek || totalCalc.domesticAllowanceFullDayTaxFreeAmount))}</span>
          </div>
        )}

        {totalCalc.domesticAllowanceHalfDayTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('HALF_DAY') || 'Half Day'})</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.domesticAllowanceHalfDayTaxFreeAmount, totalCalc.domesticAllowanceHalfDayTaxFreeAmountInSek || totalCalc.domesticAllowanceHalfDayTaxFreeAmount))}</span>
          </div>
        )}

        {totalCalc.travelCostsAccordingTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('TRAVEL_COSTS_ACCORDING_TO_ATTACHED_PDF') || 'Travel costs according to attached PDF'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.travelCostsAccordingTaxFreeAmount, totalCalc.travelCostsAccordingTaxFreeAmountInSek || totalCalc.travelCostsAccordingTaxFreeAmount))}</span>
          </div>
        )}

        {totalCalc.expense > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('EXPENSE') || 'Travel Expenses as per attached PDF'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.expense, totalCalc.expenseInSek || totalCalc.expense))}</span>
          </div>
        )}

        {totalCalc.administratorFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ADMINISTRATOR_FEE') || 'Administrator Fee'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.administratorFee, totalCalc.administratorFeeInSek || totalCalc.administratorFee))}</span>
          </div>
        )}
      </div>

      {/* Step 16: Net salary incl. holiday pay */}
      <div className="borderTopCheck"></div>
      <div className="breakdown-section net-salary-section">
        <div className="calculation-item">
          <span className="item-label item-label-bold">{t('NET_SALARY') || 'Net salary incl. holiday pay'}</span>
          <span className="item-value">{formatCurrency(getAmount(totalCalc.net, totalCalc.netInSek || totalCalc.net))}</span>
        </div>
      </div>
      <div className="borderBottomCheck"></div>

      {/* Individual Freelancer Breakdowns - Following calculation order */}
      {freelancersData.map((freelancerData: any, index: number) => (
        <div key={index} className="freelancer-breakdown">
          <div className="freelancer-name-header">
            <span className="cbm-freelancer-avatar" aria-hidden="true">
              {getInitials(freelancerData.freelancerName) || (index + 1)}
            </span>
            <h6>{freelancerData.freelancerName || `Freelancer ${index + 1}`}</h6>
          </div>

          <div className="breakdown-subsection">
            {/* Step 1: Invoice amount inc. VAT */}
            <div className="calculation-item">
              <span className="item-label">{t('INVOICE_AMOUNT_INCLUDING_VAT') || 'Invoice Amount inc. VAT'}</span>
              <span className="item-value">{formatCurrencyOriginal(freelancerData.amountIncludingVat)}</span>
            </div>

            {/* Step 2: RUT/ROT Deduction */}
            {freelancerData.rutDeductionAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction'}</span>
                <span className="item-value">-{formatCurrency(freelancerData.rutDeductionAmount)}</span>
              </div>
            )}

            {freelancerData.rootDeductionAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction'}</span>
                <span className="item-value">-{formatCurrency(freelancerData.rootDeductionAmount)}</span>
              </div>
            )}

            {/* Step 3: Customer will pay */}
            {(freelancerData.rutDeductionAmount > 0 || freelancerData.rootDeductionAmount > 0) && (
              <div className="calculation-item">
                <span className="item-label">{t('CUSTOMER_WILL_PAY') || 'Customer will pay'}</span>
                <span className="item-value">{formatCurrency(freelancerData.amountIncludingVatAfterHouseholdServiceDeduction)}</span>
              </div>
            )}

            {/* Step 4: VAT */}
            {freelancerData.vat > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('VAT') || 'VAT'} ({freelancerData.vatPercentage}%)</span>
                <span className="item-value">{formatCurrencyOriginal(freelancerData.vat)}</span>
              </div>
            )}

            {/* Step 5: Invoice amount ex. VAT */}
            <div className="calculation-item">
              <span className="item-label">{t('INVOICE_AMOUNT_EXCLUDING_VAT') || 'Invoice amount ex. VAT'}</span>
              <span className="item-value">{formatCurrency(getAmount(freelancerData.amount, freelancerData.amountInSek || freelancerData.amount))}</span>
            </div>

            {/* Step 6: Invozio percent fee */}
            {freelancerData.fee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('INVOZIO_FEE') || 'Invozio percent fee'} ({freelancerData.fakturaFeePercentage}%)</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.fee, freelancerData.feeInSEK || freelancerData.fee))}</span>
              </div>
            )}

            {/* Step 7: Loyalty discount */}
            {freelancerData.loyaltyDiscountPercentage > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('LOYALTY_DISCOUNT_ON_INVOZIO_FEE') || 'Loyalty discount on invozio fee'}</span>
                <span className="item-value">{freelancerData.loyaltyDiscountPercentage}%</span>
              </div>
            )}

            {/* Step 8: Social Fee */}
            {freelancerData.socialFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">
                  {t('SOCIAL_FEE') || 'Social Fee'} ({getSocialFeeDisplayRate(freelancerData.socialFeeRateDeduction).toFixed(2)}%)
                </span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.socialFee, freelancerData.socialFeeInSek || freelancerData.socialFee))}</span>
              </div>
            )}

            {/* Step 9: Pension Social Fee */}
            {freelancerData.pensionSocialFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('PENSION_SOCIAL_FEE') || 'Pension Social Fee'} ({freelancerData.pensionFeePer}%)</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.pensionSocialFee, freelancerData.pensionSocialFeeInSek || freelancerData.pensionSocialFee))}</span>
              </div>
            )}

            {/* Step 10: Pension */}
            {freelancerData.pension > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('PENSION') || 'Pension'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.pension, freelancerData.pensionInSek || freelancerData.pension))}</span>
              </div>
            )}

            {/* Step 11: Gross salary incl. holiday pay */}
            <div className="calculation-item">
              <span className="item-label">{t('GROSS_SALARY_INCLUDING_HOLIDAY_PAY') || 'Gross salary incl. holiday pay'}</span>
              <span className="item-value">{formatCurrency(getAmount(freelancerData.gross, freelancerData.grossInSek || freelancerData.gross))}</span>
            </div>

            {/* Step 12: Tax */}
            {freelancerData.tax > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('INCOME_TAX') || 'Tax'} ({freelancerData.taxPercentage ?? freelancerData.taxRateDeduction ?? '30.00'}%)</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.tax, freelancerData.taxInSek || freelancerData.tax))}</span>
              </div>
            )}

            {/* Step 13: Holiday Pay */}
            {freelancerData.holidayPayAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('HOLIDAY_PAY') || 'Holiday Pay'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.holidayPayAmount, freelancerData.holidayPayAmountInSek || freelancerData.holidayPayAmount))}</span>
              </div>
            )}

            {/* Step 14: Express Fee */}
            {freelancerData.expressFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('EXPRESS_FEE') || 'Express Fee'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.expressFee, freelancerData.expressFeeInSek || freelancerData.expressFee))}</span>
              </div>
            )}

            {/* Step 15: Total Working Hours */}
            {freelancerData.workingHours && freelancerData.workingHours.length > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('TOTAL_WORKING_HOURS') || 'Total Working Hours'}</span>
                <span className="item-value">{freelancerData.workingHours.reduce((sum: number, wh: any) => sum + (wh.workingHour || 0), 0)} {t('HOURS') || 'Hours'}</span>
              </div>
            )}

            {/* Step 16: Tax-free articles */}
            {freelancerData.mileageTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('MILEAGE_REIMBURSEMENT') || 'Mileage Reimbursement'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.mileageTaxFreeAmount, freelancerData.mileageTaxFreeAmountInSek || freelancerData.mileageTaxFreeAmount))}</span>
              </div>
            )}

            {freelancerData.expenditureForMaterialsAndToolsTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('EXPENSES_FOR_MATERIALS_AND_TOOLS') || 'Material Tool & Expenses'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.expenditureForMaterialsAndToolsTaxFreeAmount, freelancerData.expenditureForMaterialsAndToolsTaxFreeAmountInSek || freelancerData.expenditureForMaterialsAndToolsTaxFreeAmount))}</span>
              </div>
            )}

            {freelancerData.allowanceAbroadTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.allowanceAbroadTaxFreeAmount, freelancerData.allowanceAbroadTaxFreeAmountInSek || freelancerData.allowanceAbroadTaxFreeAmount))}</span>
              </div>
            )}

            {freelancerData.domesticAllowanceFullDayTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('FULL_DAY') || 'Full Day'})</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.domesticAllowanceFullDayTaxFreeAmount, freelancerData.domesticAllowanceFullDayTaxFreeAmountInSek || freelancerData.domesticAllowanceFullDayTaxFreeAmount))}</span>
              </div>
            )}

            {freelancerData.domesticAllowanceHalfDayTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('HALF_DAY') || 'Half Day'})</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.domesticAllowanceHalfDayTaxFreeAmount, freelancerData.domesticAllowanceHalfDayTaxFreeAmountInSek || freelancerData.domesticAllowanceHalfDayTaxFreeAmount))}</span>
              </div>
            )}

            {freelancerData.travelCostsAccordingTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('TRAVEL_COSTS_ACCORDING_TO_ATTACHED_PDF') || 'Travel costs according to attached PDF'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.travelCostsAccordingTaxFreeAmount, freelancerData.travelCostsAccordingTaxFreeAmountInSek || freelancerData.travelCostsAccordingTaxFreeAmount))}</span>
              </div>
            )}

            {freelancerData.allowance > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE') || 'Other Allowances'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.allowance, freelancerData.allowanceInSek || freelancerData.allowance))}</span>
              </div>
            )}

            {freelancerData.expense > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('EXPENSE') || 'Travel Expenses as per attached PDF'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.expense, freelancerData.expenseInSek || freelancerData.expense))}</span>
              </div>
            )}

            {freelancerData.administratorFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ADMINISTRATOR_FEE') || 'Administrator Fee'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.administratorFee, freelancerData.administratorFeeInSek || freelancerData.administratorFee))}</span>
              </div>
            )}
          </div>

          {/* Step 17: Net salary incl. holiday pay */}
          <div className="breakdown-subsection net-salary-subsection">
            <div className="calculation-item">
              <span className="item-label item-label-bold">{t('NET_SALARY') || 'Net salary incl. holiday pay'}</span>
              <span className="item-value">{formatCurrency(getAmount(freelancerData.net, freelancerData.netInSek || freelancerData.net))}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderCalculationData = (calcData: any, index?: number) => (
    <div className="calculation-list" key={index}>
      {index !== undefined && (
        <div className="freelancer-header">
          <h5><b>Freelancer {index + 1} (Name: {calcData.freelancerName})</b></h5>
        </div>
      )}
      
      <div className="calculation-item">
        <span className="item-label">{t('INVOICE_AMOUNT_INC_VAT') || 'Invoice amount inc. VAT'}</span>
        <span className="item-value">{formatCurrencyOriginal(calcData.amountIncludingVat)}</span>
      </div>

      {calcData.vat > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('VAT') || 'VAT'} ({calcData.vatPercentage}%)</span>
          <span className="item-value">{formatCurrencyOriginal(calcData.vat)}</span>
        </div>
      )}

      <div className="calculation-item">
        <span className="item-label">{t('INVOICE_AMOUNT_EX_VAT') || 'Invoice amount ex. VAT'}</span>
        <span className="item-value">{formatCurrency(calcData.amount)}</span>
      </div>

      {calcData.fee > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('INVOZIO_PERCENT_FEE') || 'Invozio percent fee'} ({calcData.fakturaFeePercentage}%)</span>
          <span className="item-value">{formatCurrency(calcData.fee)}</span>
        </div>
      )}

      {calcData.socialFee > 0 && (
        <div className="calculation-item">
          <span className="item-label">
            {t('SOCIAL_FEE') || 'Social Fee'} ({getSocialFeeDisplayRate(calcData.socialFeeRateDeduction).toFixed(2)}%)
          </span>
          <span className="item-value">{formatCurrency(calcData.socialFee)}</span>
        </div>
      )}

      {calcData.pensionSocialFee > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('PENSION_SOCIAL_FEE') || 'Pension Social Fee'}</span>
          <span className="item-value">{formatCurrency(calcData.pensionSocialFee)}</span>
        </div>
      )}

      {calcData.pension > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('PENSION') || 'Pension'}</span>
          <span className="item-value">{formatCurrency(calcData.pension)}</span>
        </div>
      )}

      <div className="calculation-item highlight">
        <span className="item-label">{t('GROSS_SALARY_INCL_HOLIDAY_PAY') || 'Gross salary incl. holiday pay'}</span>
        <span className="item-value">{formatCurrency(calcData.gross)}</span>
      </div>

      {calcData.tax > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('TAX') || 'Tax'} ({calcData.taxPercentage || '30.00'}%)</span>
          <span className="item-value">{formatCurrency(calcData.tax)}</span>
        </div>
      )}

      {calcData.holidayPayAmount > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('HOLIDAY_PAY') || 'Holiday Pay'}</span>
          <span className="item-value">{formatCurrency(calcData.holidayPayAmount)}</span>
        </div>
      )}

      {calcData.expressFee > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('EXPRESS_FEE') || 'Express Fee'}</span>
          <span className="item-value">{formatCurrency(calcData.expressFee)}</span>
        </div>
      )}

      {calcData.workingHours && calcData.workingHours.length > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('TOTAL_WORKING_HOURS') || 'Total Working Hours'}</span>
          <span className="item-value">{calcData.workingHours.reduce((sum: number, wh: any) => sum + (wh.workingHour || 0), 0)} Hours</span>
        </div>
      )}

      <div className="calculation-item highlight">
        <span className="item-label">{t('NET_SALARY_INCL_HOLIDAY_PAY') || 'Net salary incl. holiday pay'}</span>
        <span className="item-value">{formatCurrency(calcData.net)}</span>
      </div>

      {calcData.toPay > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('TO_PAY') || 'To Pay'}</span>
          <span className="item-value">{formatCurrency(calcData.toPay)}</span>
        </div>
      )}
    </div>
  );

  // Check if we have multiple freelancers
  const hasMultipleFreelancers = data?.freelancersData && data.freelancersData.length > 1;

  return (
    <Modal 
      show={isOpen} 
      onHide={onClose} 
      size="lg" 
      centered
      className="calculation-breakdown-modal"
      dialogClassName="modal-90vh"
    >
      <Modal.Header closeButton>
        <Modal.Title className="modal-title-text">
          {t('HOW_WE_CALCULATE_YOUR_INVOICE') || 'How We Calculate Your Invoice'}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* Tabs: list breakdown vs chart */}
        {data && (
          <div className={`cbm-tabs${indicator.width > 0 ? ' has-indicator' : ''}`} role="tablist" ref={tabsRef}>
            <span
              aria-hidden="true"
              className={`cbm-tab-indicator${indicatorReady ? ' is-animated' : ''}`}
              style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
            />
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'breakdown'}
              className={`cbm-tab${activeTab === 'breakdown' ? ' active' : ''}`}
              onClick={() => setActiveTab('breakdown')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
              {t('BREAKDOWN') || 'Breakdown'}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'chart'}
              className={`cbm-tab${activeTab === 'chart' ? ' active' : ''}`}
              onClick={() => setActiveTab('chart')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 21H4a1 1 0 0 1-1-1V3" /><path d="M7 14l4-4 3 3 5-6" />
              </svg>
              {t('CHART') || 'Chart'}
            </button>
          </div>
        )}

        {data && activeTab === 'chart' && renderChartView()}

        {data ? (
          <div className="breakdown-content" style={{ display: activeTab === 'breakdown' ? undefined : 'none' }}>
            {hasMultipleFreelancers ? (
              // Multiple freelancers: Show Invoice Summary + Individual Breakdowns
              data.totalCalculation && data.freelancersData ? (
                renderMultipleFreelancersView(data.totalCalculation, data.freelancersData)
              ) : (
                <div className="no-data">
                  <h5>{t('NO_DATA_AVAILABLE') || 'No data available'}</h5>
                </div>
              )
            ) : (
              // Single freelancer: Show structured breakdown
              data.totalCalculation ? (
                renderSingleFreelancerView(data.totalCalculation)
              ) : (
                <div className="no-data">
                  <h5>{t('NO_DATA_AVAILABLE') || 'No data available'}</h5>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="no-data">
            <h5>{t('NO_CALCULATION_DATA') || 'No calculation data available'}</h5>
            <p>{t('NO_CALCULATION_DATA_DESC') || 'Please ensure you have entered all required information and try again.'}</p>
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          style={{
            backgroundColor: "#6750b8",
            borderRadius: "25px",
            boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.04)",
            display: "flex",
            padding: "10px 18px",
            justifyContent: "center",
            alignItems: "center",
            gap: "6px",
            alignSelf: "stretch",
            border: "none"
          }} 
          onClick={onClose}
        >
          {t('CLOSE') || 'Close'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CalculationBreakdownModal;